import { apiCall } from '../../../Middleware/API/apiRequest';
import { getTokensFromStorage } from '../../../Utils/Auth/AuthToken';

const { accessToken } = getTokensFromStorage();
const projectsRouteName = '/rollouts/projects';

/**
 * Lists Project.
 * @returns {Promise} - A promise that resolves with the list of users or rejects with an error.
 * @param props
 */
export const listProjects = async ({
  limit,
  offset,
  filter,
  sort,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const data = {
    Filters: filter,
    pageNumber: offset === 0 ? 1 : offset,
    pageSize: limit,
    sortField: sort,
  };
  const response = await apiRequest({
    url: `${projectsRouteName}`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: JSON.stringify(data),
    accessToken,
  });
  if (!response?.isSuccess) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(
        headers,
        isServiceWorker
      );
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }
  return {
    isSuccess: response?.isSuccess,
    projects: response?.payload,
    TotalCount: response?.totalRecordCount,
  };
};

/**
 * get all causes.
 * @returns {Promise} - A promise that resolves with the get all causes or rejects with an error.
 * @param props
 */
export const getAllCauses = async (projectId, versionId, action, name) => {
  const url =
    `/issues/${projectId}/${versionId}/${action}` +
    (name ? `/${name}` : '');
  const response = await apiRequest({
    url: url,
    method: 'GET',
    accessToken,
  });
  return response;
};
/**
 * Get Projects.
 * @returns {Promise} - A promise that resolves with the list of users or rejects with an error.
 * @param props
 */
// api/projects.js
export const getProjects = async (
  {
    pageNumber = 1,
    pageSize = 50,
    search = '',
    sortBy = 'name',
    sortOrder = 'asc',
  } = {},
  isServiceWorker = false
) => {
  const qs = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
    search: search ?? '',
    sortBy,
    sortOrder,
  });

  const response = await apiRequest({
    url: `/projects?${qs.toString()}`,
    method: 'GET',
    accessToken,
  });
  const ok =
    response?.isSuccess === true ||
    response?.success === true ||
    response?.status?.code === 0;
  if (!ok) {
    if (isServiceWorker) {
      const cacheResponse = await getGraphqlCacheResponse(headers, isServiceWorker);
      return cacheResponse;
    }
    return {
      isSuccess: false,
      message: response?.message || response?.status?.message || 'Failed to fetch projects',
    };
  }

  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }

  const items = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.payload)
    ? response.payload
    : [];

  const total =
    response?.pagination?.total ??
    response?.totalRecordCount ??
    (Array.isArray(items) ? items.length : 0);

  const outPageNumber = response?.pagination?.pageNumber ?? pageNumber;
  const outPageSize = response?.pagination?.pageSize ?? pageSize;
  const outHasNext =
    response?.pagination?.hasNextPage ?? outPageNumber * outPageSize < total;

  return {
    isSuccess: true,
    projects: items,
    totalCount: total,
    pageNumber: outPageNumber,
    pageSize: outPageSize,
    hasNextPage: outHasNext,
  };
};

/**
 * Lists Versions.
 * @returns {Promise} - A promise that resolves with the list of version or rejects with an error.
 * @param props
 */
export const listVersions = async ({ projectKey, isServiceWorker }) => {
  const response = await apiRequest({
    url: `/projects/${projectKey}/versions`,
    method: 'GET',
    accessToken,
  });
  if (!response?.isSuccess) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(
        headers,
        isServiceWorker
      );
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }

  return {
    isSuccess: response?.isSuccess,
    versions: response.payload?.versions,
    totalCount: response?.totalRecordCount,
  };
};

/**
 * get rollout name.
 * @returns {Promise} - A promise that resolves with the get rollout name or rejects with an error.
 * @param props
 */
export const getRolloutName = async (projectId, versionId, action, major) => {
  const response = await apiRequest({
    url: `/rollouts/name/${projectId}/${versionId}/${action}/${major}`,
    method: 'GET',
    accessToken,
  });
  return response;
};

/**
 * save Rollout.
 * @returns {Promise} - A promise that resolves save Rollout or rejects with an error.
 * @param props
 */
export const saveRollout = async (data) => {
  let response = await apiRequest({
    url: `/rollouts`,
    method: 'POST',
    body: JSON.stringify(data),
    accessToken,
  });
  return response;
};

/**
 * Sync projects from provider into SDOProjects.
 * @returns {Promise} - ApiResponse style result
 */
export const syncProjects = async () => {
  const response = await apiRequest({
    url: `/rollouts/projects/sync`,
    method: 'GET',
    accessToken,
  });
  return response;
};

/**
 * Api Call Wrapper Method.
 * @param {object} api - Contains details like url headers method and data.
 * @returns {Promise} - A promise that resolves with the data or rejects with an error.
 */
const apiRequest = async (request) => {
  let response;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  request.headers = headers;
  try {
    response = await apiCall(request);
    if (!response.isSuccess) {
      return { isSuccess: false, message: response?.status?.message };
    }
    return response;
  } catch (e) {
    response = { isSuccess: false, message: e.message };
  }
  return response;
};
